import { useCallback, useEffect, useMemo, useState } from "react"
import {
  activeFilters as computeActiveFilters,
  defaultPostFilters,
  isEmptyPostFilters,
  normalizePostFilters,
  removeFilter as removeFilterFromState,
  resetCategory as resetCategoryOf,
  type ActiveFilter,
  type FilterCategory,
  type PostFilterState,
} from "@/nostr/api/filters/filterEngine"
import { readStorage, StorageKeys, writeStorage } from "@/utils/storage"

export type PostFilterPatch = Partial<PostFilterState>

export interface PostFiltersApi {
  /** The single source of truth for every filter of this list. */
  filters: PostFilterState
  /** Shallow merge; unknown values are normalised, so callers cannot corrupt the state. */
  patch: (patch: PostFilterPatch) => void
  /** Reset one section only. */
  resetCategory: (category: FilterCategory) => void
  /** Remove one active chip. */
  remove: (key: string) => void
  /** Reset every section (Clear all). */
  clearAll: () => void
  active: ActiveFilter[]
  activeCount: number
  isDefault: boolean
}

type StoredFilters = Record<string, unknown>

function readStored(pageKey: string): unknown {
  const stored = readStorage<StoredFilters>(StorageKeys.postFilters, {})
  return stored?.[pageKey]
}

/**
 * Per-page filter state.
 *
 * The state is kept per page key (`feed`, `explore`, `relay`, `search`,
 * `bookmarks`, `profile`, …) so filters never leak between unrelated lists, and
 * it is written back to the existing settings storage namespace, which restores
 * the user's filters when they come back to a page.
 */
export function usePostFilters(pageKey: string): PostFiltersApi {
  const [filters, setFilters] = useState<PostFilterState>(() =>
    normalizePostFilters(readStored(pageKey)),
  )

  // Restore when the page key changes (same component, different list).
  useEffect(() => {
    setFilters(normalizePostFilters(readStored(pageKey)))
  }, [pageKey])

  useEffect(() => {
    const stored = readStorage<StoredFilters>(StorageKeys.postFilters, {})
    writeStorage(StorageKeys.postFilters, { ...stored, [pageKey]: filters })
  }, [pageKey, filters])

  const patch = useCallback((next: PostFilterPatch) => {
    setFilters((current) => normalizePostFilters({ ...current, ...next }))
  }, [])

  const resetCategory = useCallback((category: FilterCategory) => {
    setFilters((current) => resetCategoryOf(current, category))
  }, [])

  const remove = useCallback((key: string) => {
    setFilters((current) => removeFilterFromState(current, key))
  }, [])

  const clearAll = useCallback(() => setFilters(defaultPostFilters()), [])

  const active = useMemo(() => computeActiveFilters(filters), [filters])

  return {
    filters,
    patch,
    resetCategory,
    remove,
    clearAll,
    active,
    activeCount: active.length,
    isDefault: isEmptyPostFilters(filters) && filters.sort === "latest",
  }
}
